import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import { getRecitalTypeForDiscussion } from '../recitalTypes';

export default class PublishRecitalModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);

    this.discussion = this.attrs.discussion;
    this.recitalType = getRecitalTypeForDiscussion(this.discussion);

    // Extract recital number from title
    // Priority: #N (e.g., "Recital #7", "PIYW#50") then standalone number (e.g., "2026")
    const hashMatch = this.discussion.title().match(/#(\d+)/);
    const standaloneMatch = this.discussion.title().match(/\b(\d+)\b/);
    const extractedNumber = hashMatch
      ? parseInt(hashMatch[1], 10)
      : (standaloneMatch ? parseInt(standaloneMatch[1], 10) : 1);

    // Wizard page: 1 = Recital Details, 2 = Review & Publish
    this._page = 1;

    // Meta discussion state
    this.metaDiscussionId = '';
    this.metaSearchQuery = '';
    this.metaSearchResults = [];
    this.metaSearchLoading = false;
    this.metaSelectedTitle = '';

    // Placeholder values keyed by backend tag (e.g. "auto_recital_number", "dropbox_url")
    this._placeholders = {};
    // Placeholder metadata from dry-run: { key: { category, label } }
    this._placeholderInfo = {};

    // Seed auto_recital_number so the initial dry-run uses the right value
    this._placeholders.auto_recital_number = String(extractedNumber);

    // Page 2 editable fields
    this.editableTitle = '';
    this.editableIndex = '';
    this.editableClosing = '';

    // Page 2 settings
    this.autoGenerateIndex = this.recitalType ? this.recitalType.auto_generate_index !== false : true;
    this.addClosingPost = true;
    this.reservedSlots = this.recitalType ? (this.recitalType.reserved_slots != null ? this.recitalType.reserved_slots : 5) : 0;
    this.stickyOnPublish = true;
    this.confirmed = false;

    // Preview staleness tracking
    this._previewEdited = false;
    this._lastSentValues = null;      // snapshot used for "did values change?" in _onNextClick
    this._lastRenderedValues = null;   // snapshot of values that produced the current preview

    // API state
    this.dryRunResult = null;
    this.dryRunLoading = false;
    this.publishing = false;
    this.publishError = null;

    // Auto-detect meta discussion from submission thread title
    this.autoDetectMeta();

    // Initial dry-run to populate Page 1 placeholders and submissions
    this.performDryRun();
  }

  className() {
    return 'PublishRecitalModal Modal--large';
  }

  title() {
    return app.translator.trans('pianotell-recitals.forum.publish_modal_title');
  }

  content() {
    return m('.Modal-body', [
      this._page === 1 ? this._renderPage1() : this._renderPage2(),
    ]);
  }

  // ---------------------------------------------------------------------------
  // Page 1 — Recital Details
  // ---------------------------------------------------------------------------

  _renderPage1() {
    const allPlaceholders = [];

    for (const key in this._placeholderInfo) {
      const info = this._placeholderInfo[key];
      if (info.category === 'auto' || info.category === 'placeholder') {
        allPlaceholders.push({ key, label: info.label, category: info.category });
      }
      // internal placeholders are NOT rendered as fields
    }

    return [
      // 1. Submissions list (first item in the dialog)
      this._renderSubmissionsList(),

      // 2. Meta Feedback Discussion
      m('.Form-group', [
        m('label', app.translator.trans('pianotell-recitals.forum.publish_modal_meta_thread_id')),
        m('.helpText', app.translator.trans('pianotell-recitals.forum.publish_modal_meta_thread_help', { placeholder: '{internal_meta_link}' })),
        this._renderMetaSelector(),
      ]),

      // 3. Placeholder fields (auto + user)
      allPlaceholders.map((ph) =>
        m('.Form-group', { key: ph.key }, [
          m('label', [
            ph.label,
            ph.category === 'auto'
              ? m('span', { style: 'color: #6b7280; font-size: 11px; margin-left: 6px;' }, '(auto)')
              : null,
          ]),
          m('input.FormControl', {
            type: 'text',
            value: this._placeholders[ph.key] || '',
            oninput: (e) => {
              this._placeholders[ph.key] = e.target.value;
            },
          }),
        ])
      ),

      // 3. Next button
      m('.Form-group', { style: 'text-align: right;' }, [
        Button.component(
          {
            className: 'Button Button--primary',
            loading: this.dryRunLoading,
            disabled: this.dryRunLoading,
            onclick: () => this._onNextClick(),
          },
          'Next \u2192'
        ),
      ]),
    ];
  }

  _renderSubmissionsList() {
    if (this.dryRunLoading && !this.dryRunResult) {
      return m('.Form-group', { style: 'text-align: center; padding: 10px;' },
        m(LoadingIndicator, { size: 'small' })
      );
    }

    if (!this.dryRunResult || !this.dryRunResult.entries) return null;

    const submissionWarnings = (this.dryRunResult.warnings || []).filter(
      (w) => typeof w === 'string'
    );

    return m('.Form-group', [
      m('label', { style: 'font-weight: bold;' },
        'Submissions (' + this.dryRunResult.entries.length + ')'
      ),
      m('ol', { style: 'margin: 8px 0; font-size: 13px;' },
        this.dryRunResult.entries.map((entry) =>
          m('li', entry.username + (entry.piece ? ' \u2014 ' + entry.piece : ''))
        )
      ),
      submissionWarnings.length > 0
        ? m('.Alert.Alert--warning', { style: 'margin-top: 4px;' },
            submissionWarnings.map((w) => m('div', w))
          )
        : null,
    ]);
  }

  // ---------------------------------------------------------------------------
  // Page 2 — Review & Publish
  // ---------------------------------------------------------------------------

  _renderPage2() {
    // Show full loading only if we have no result yet (shouldn't normally happen
    // since Next waits for the dry-run response before switching pages)
    if (!this.dryRunResult) {
      return m('.Form-group', { style: 'text-align: center; padding: 20px;' }, m(LoadingIndicator));
    }

    const stale = this._isStalePreview();
    // Filter warnings: only show for placeholders still present in the coordinator's edited preview
    const allEditedText = (this.editableTitle || '') + '\n' + (this.editableIndex || '') + '\n' + (this.editableClosing || '');
    const placeholderWarnings = (this.dryRunResult.warnings || []).filter(
      (w) => typeof w === 'object' && w.type === 'empty_placeholder'
        && allEditedText.includes('{' + w.tag + '}')
    );

    return [
      // 1. Title — editable, pre-filled from dry-run
      m('.Form-group', [
        m('label', app.translator.trans('pianotell-recitals.forum.publish_modal_title_preview')),
        m('input.FormControl', {
          type: 'text',
          value: this.editableTitle,
          oninput: (e) => {
            this.editableTitle = e.target.value;
            this._previewEdited = true;
          },
        }),
      ]),

      // 2. Auto-generate index post checkbox
      m('.Form-group', [
        m(
          'label.checkbox',
          m('input', {
            type: 'checkbox',
            checked: this.autoGenerateIndex,
            onchange: (e) => {
              this.autoGenerateIndex = e.target.checked;
              if (this.dryRunResult) {
                this.editableIndex = e.target.checked
                  ? (this.dryRunResult.index_preview || '')
                  : (this.dryRunResult.original_post1 || '');
              }
            },
          }),
          ' ',
          app.translator.trans('pianotell-recitals.forum.publish_modal_auto_index')
        ),
        m('.helpText', app.translator.trans('pianotell-recitals.forum.publish_modal_auto_index_help')),
      ]),

      // 3. Index post preview — editable textarea
      m('.Form-group', [
        m('label', app.translator.trans('pianotell-recitals.forum.publish_modal_index_preview')),
        m('textarea.FormControl', {
          rows: 12,
          style: 'white-space: pre-wrap; font-size: 13px; font-family: monospace;',
          value: this.editableIndex,
          oninput: (e) => {
            this.editableIndex = e.target.value;
            this._previewEdited = true;
          },
        }),
      ]),

      // 4. Add closing post checkbox
      m('.Form-group', [
        m(
          'label.checkbox',
          m('input', {
            type: 'checkbox',
            checked: this.addClosingPost,
            onchange: (e) => {
              this.addClosingPost = e.target.checked;
            },
          }),
          ' ',
          app.translator.trans('pianotell-recitals.forum.publish_modal_thank_you')
        ),
      ]),

      // 5. Closing post preview — editable textarea (only if checkbox checked)
      this.addClosingPost
        ? m('.Form-group', [
            m('label', app.translator.trans('pianotell-recitals.forum.publish_modal_closing_preview')),
            m('textarea.FormControl', {
              rows: 4,
              style: 'font-size: 13px;',
              value: this.editableClosing,
              oninput: (e) => {
                this.editableClosing = e.target.value;
                this._previewEdited = true;
              },
            }),
          ])
        : null,

      // 6. Reserved slots
      m('.Form-group', [
        m('label', app.translator.trans('pianotell-recitals.forum.publish_modal_reserved_slots')),
        m('input.FormControl', {
          type: 'number',
          min: 0,
          max: 10,
          value: this.reservedSlots,
          oninput: (e) => {
            this.reservedSlots = Math.min(parseInt(e.target.value) || 0, 10);
          },
        }),
        m('.helpText', app.translator.trans('pianotell-recitals.forum.publish_modal_reserved_slots_help')),
      ]),

      // 7. Sticky on publish
      m('.Form-group', [
        m(
          'label.checkbox',
          m('input', {
            type: 'checkbox',
            checked: this.stickyOnPublish,
            onchange: (e) => {
              this.stickyOnPublish = e.target.checked;
            },
          }),
          ' ',
          app.translator.trans('pianotell-recitals.forum.publish_modal_sticky')
        ),
      ]),

      // 8. Refresh button — shown only when Page 1 values changed since last preview
      stale
        ? m('.Form-group', [
            Button.component(
              {
                className: 'Button',
                loading: this.dryRunLoading,
                onclick: () => this._onRefreshClick(),
              },
              this._previewEdited
                ? '\u26A0\uFE0F Refresh \u2014 your edits will be overwritten'
                : '\u{1F504} Refresh Preview'
            ),
          ])
        : null,

      // 9. Empty placeholder warnings
      placeholderWarnings.length > 0
        ? m('.Form-group',
            placeholderWarnings.map((w) =>
              m('.Alert.Alert--warning', { style: 'margin-bottom: 4px;' },
                '\u26A0 ' + w.label + ' is empty but referenced in templates'
              )
            )
          )
        : null,

      m('hr'),

      // 10. Confirmation checkbox + Publish button
      m('.Form-group', [
        m(
          'label.checkbox',
          m('input', {
            type: 'checkbox',
            checked: this.confirmed,
            onchange: (e) => {
              this.confirmed = e.target.checked;
            },
          }),
          ' ',
          app.translator.trans('pianotell-recitals.forum.publish_modal_confirm_checkbox')
        ),
      ]),

      this.publishError
        ? m('.Alert.Alert--error', this.publishError)
        : null,

      m('.Form-group', { style: 'display: flex; justify-content: space-between;' }, [
        // 11. Back button
        Button.component(
          {
            className: 'Button',
            onclick: () => { this._page = 1; },
          },
          '\u2190 Back'
        ),

        // Publish button
        Button.component(
          {
            className: 'Button Button--danger',
            loading: this.publishing,
            disabled: !this.confirmed || this.dryRunLoading,
            onclick: () => this.performPublish(),
          },
          app.translator.trans('pianotell-recitals.forum.publish_modal_confirm')
        ),
      ]),
    ];
  }

  // ---------------------------------------------------------------------------
  // Meta discussion selector
  // ---------------------------------------------------------------------------

  _renderMetaSelector() {
    if (this.metaSelectedTitle) {
      return m('.MetaDiscussion-selected', [
        m('span.MetaDiscussion-title', [
          m('i.icon.fas.fa-check', { style: 'color: #059669; margin-right: 6px' }),
          '#' + this.metaDiscussionId + ': ' + this.metaSelectedTitle,
        ]),
        m(
          'button.Button.Button--link.Button--sm',
          {
            onclick: (e) => {
              e.preventDefault();
              e.stopPropagation();
              this.metaDiscussionId = '';
              this.metaSelectedTitle = '';
            },
          },
          m('i.icon.fas.fa-times')
        ),
      ]);
    }

    return [
      m('input.FormControl', {
        type: 'text',
        placeholder: 'Search discussions...',
        value: this.metaSearchQuery,
        oninput: (e) => {
          this.metaSearchQuery = e.target.value;
          this.searchMetaDiscussions();
        },
      }),
      this.metaSearchLoading ? m(LoadingIndicator, { size: 'small' }) : null,
      this.metaSearchResults.length > 0
        ? m(
            'ul.MetaDiscussion-results',
            { style: 'list-style: none; padding: 0; margin: 5px 0; border: 1px solid #e5e7eb; border-radius: 4px; max-height: 200px; overflow-y: auto;' },
            this.metaSearchResults.map((d) =>
              m(
                'li',
                {
                  style: 'padding: 8px 12px; cursor: pointer; border-bottom: 1px solid #f3f4f6;',
                  onclick: () => {
                    this.metaDiscussionId = d.id;
                    this.metaSelectedTitle = d.title;
                    this.metaSearchResults = [];
                    this.metaSearchQuery = '';
                  },
                  onmouseenter: (e) => { e.target.style.background = '#f3f4f6'; },
                  onmouseleave: (e) => { e.target.style.background = ''; },
                },
                '#' + d.id + ': ' + d.title
              )
            )
          )
        : null,
    ];
  }

  // ---------------------------------------------------------------------------
  // Navigation
  // ---------------------------------------------------------------------------

  _onNextClick() {
    const snapshot = this._getPage1Snapshot();

    // If nothing changed since last dry-run, skip re-fetch and preserve edits
    if (snapshot === this._lastSentValues) {
      this._page = 2;
      return;
    }

    // If coordinator hasn't edited Page 2 previews, regenerate silently
    if (!this._previewEdited) {
      this._lastSentValues = snapshot;
      this.performDryRun(() => {
        this._populatePage2FromDryRun();
        this._page = 2;
        this._previewEdited = false;
      });
      return;
    }

    // Coordinator has edited Page 2 — ask what to do
    const choice = confirm(
      'You have edited the preview on Page 2. Click OK to regenerate (your edits will be overwritten), or Cancel to keep your edits as-is.'
    );

    if (choice) {
      // OK → regenerate, overwrite edits
      this._lastSentValues = snapshot;
      this.performDryRun(() => {
        this._populatePage2FromDryRun();
        this._page = 2;
        this._previewEdited = false;
      });
    } else {
      // Cancel → keep edits, just go to Page 2 (preview is now stale)
      this._lastSentValues = snapshot;
      this._page = 2;
    }
  }

  _onRefreshClick() {
    const snapshot = this._getPage1Snapshot();
    this._lastSentValues = snapshot;
    this.performDryRun(() => {
      this._populatePage2FromDryRun();
      this._previewEdited = false;
    });
  }

  /**
   * Snapshot of Page 1 values that affect the dry-run output.
   * Used to detect stale previews after the user edits Page 1 fields.
   */
  _getPage1Snapshot() {
    const vals = {};
    for (const key in this._placeholderInfo) {
      const info = this._placeholderInfo[key];
      if (info.category === 'auto' || info.category === 'placeholder') {
        vals[key] = this._placeholders[key] || '';
      }
    }
    return JSON.stringify({
      placeholders: vals,
      metaDiscussionId: this.metaDiscussionId,
    });
  }

  /**
   * Returns true if current Page 1 values differ from what was last used
   * to generate the preview, meaning the Page 2 previews are stale.
   */
  _isStalePreview() {
    if (!this._lastRenderedValues) return false;
    return this._getPage1Snapshot() !== this._lastRenderedValues;
  }

  /**
   * Populate Page 2 editable fields from the latest dry-run response.
   */
  _populatePage2FromDryRun() {
    if (!this.dryRunResult) return;
    this.editableTitle = this.dryRunResult.title_preview || '';
    this.editableIndex = this.autoGenerateIndex
      ? (this.dryRunResult.index_preview || '')
      : (this.dryRunResult.original_post1 || '');
    this.editableClosing = this.dryRunResult.closing_preview || '';
  }

  // ---------------------------------------------------------------------------
  // Data methods
  // ---------------------------------------------------------------------------

  autoDetectMeta() {
    const title = this.discussion.title();
    let searchQuery = '';
    let numberToMatch = '';

    const piywMatch = title.match(/PIYW\s*#?(\d+)/i);
    const recitalMatch = title.match(/Recital\s*#(\d+)/i);

    if (piywMatch) {
      searchQuery = 'PIYW';
      numberToMatch = piywMatch[1];
    } else if (recitalMatch) {
      searchQuery = 'Recital Meta';
      numberToMatch = recitalMatch[1];
    }

    if (!searchQuery || !numberToMatch) return;

    const apiUrl = app.forum.attribute('apiUrl');
    const selfId = this.discussion.id();

    fetch(apiUrl + '/discussions?filter[q]=' + encodeURIComponent(searchQuery) + '&page[limit]=50', {
      credentials: 'same-origin',
    })
      .then((r) => r.json())
      .then((result) => {
        const numberPattern = new RegExp('#' + numberToMatch + '\\b');
        const candidates = (result.data || []).filter((d) => {
          const t = d.attributes.title;
          return numberPattern.test(t)
            && t.indexOf('Submission') === -1
            && t.indexOf('Is Now Live') === -1
            && t.indexOf('is Now Live') === -1
            && t.indexOf('is now live') === -1
            && String(d.id) !== String(selfId);
        });

        if (candidates.length === 1) {
          this.metaDiscussionId = candidates[0].id;
          this.metaSelectedTitle = candidates[0].attributes.title;
          m.redraw();
        } else if (candidates.length > 1) {
          this.metaSearchResults = candidates.map((d) => ({
            id: d.id,
            title: d.attributes.title,
          }));
          m.redraw();
        }
      })
      .catch(() => {});
  }

  searchMetaDiscussions() {
    if (this.metaSearchQuery.length < 2) {
      this.metaSearchResults = [];
      return;
    }

    clearTimeout(this._searchTimeout);
    this._searchTimeout = setTimeout(() => {
      this.metaSearchLoading = true;
      m.redraw();

      const apiUrl = app.forum.attribute('apiUrl');
      const q = encodeURIComponent(this.metaSearchQuery);

      fetch(apiUrl + '/discussions?filter[q]=' + q + '&page[limit]=8', {
        credentials: 'same-origin',
      })
        .then((r) => r.json())
        .then((result) => {
          this.metaSearchResults = (result.data || []).map((d) => ({
            id: d.id,
            title: d.attributes.title,
          }));
          this.metaSearchLoading = false;
          m.redraw();
        })
        .catch(() => {
          this.metaSearchLoading = false;
          m.redraw();
        });
    }, 300);
  }

  performDryRun(onComplete) {
    this.dryRunLoading = true;
    this.publishError = null;
    m.redraw();

    const apiUrl = app.forum.attribute('apiUrl');
    const token = app.session.csrfToken;
    const recitalNumber = parseInt(this._placeholders.auto_recital_number, 10) || 1;

    fetch(apiUrl + '/recitals/dry-run', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': token,
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        discussionId: this.discussion.id(),
        recitalNumber: recitalNumber,
        newTitle: this.discussion.title(),
        metaDiscussionId: this.metaDiscussionId ? parseInt(this.metaDiscussionId, 10) : null,
        placeholders: this._placeholders,
      }),
    })
      .then((r) => r.json())
      .then((result) => {
        this.dryRunResult = result;

        // Populate placeholder metadata and values from backend response
        if (result.placeholders) {
          for (const key in result.placeholders) {
            const ph = result.placeholders[key];

            this._placeholderInfo[key] = {
              category: ph.category,
              label: ph.label,
            };

            // Internal values always sync from backend; computed/user values
            // only set if not already edited by the coordinator
            if (ph.category === 'internal') {
              this._placeholders[key] = ph.value || '';
            } else if (this._placeholders[key] === undefined || this._placeholders[key] === '') {
              this._placeholders[key] = ph.value || '';
            }
          }
        }

        this.dryRunLoading = false;
        this._lastRenderedValues = this._getPage1Snapshot();
        if (onComplete) onComplete();
        m.redraw();
      })
      .catch((error) => {
        this.dryRunLoading = false;
        this.publishError = error.message || 'Dry run failed';
        m.redraw();
      });
  }

  performPublish() {
    if (!this.confirmed) return;

    this.publishing = true;
    this.publishError = null;

    const apiUrl = app.forum.attribute('apiUrl');
    const token = app.session.csrfToken;
    const recitalNumber = parseInt(this._placeholders.auto_recital_number, 10) || 1;

    fetch(apiUrl + '/recitals/publish', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': token,
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        discussionId: this.discussion.id(),
        recitalNumber: recitalNumber,
        newTitle: this.editableTitle,
        autoGenerateIndex: this.autoGenerateIndex,
        indexContent: this.editableIndex || null,
        metaDiscussionId: this.metaDiscussionId ? parseInt(this.metaDiscussionId, 10) : null,
        thankYouMessage: this.addClosingPost && this.editableClosing ? this.editableClosing : null,
        reservedSlots: this.reservedSlots || 0,
        stickyOnPublish: this.stickyOnPublish,
        placeholders: this._placeholders,
      }),
    })
      .then((r) => {
        if (!r.ok) throw new Error('Publish failed: ' + r.status);
        return r.json();
      })
      .then((result) => {
        this.publishing = false;
        const subs = result.published_submissions || 0;
        const deleted = result.deleted_hidden_posts || 0;
        let msg = subs + ' submission' + (subs !== 1 ? 's' : '') + ' published.';
        if (deleted > 0) {
          msg += ' ' + deleted + ' deleted post' + (deleted !== 1 ? 's' : '') + ' removed.';
        }
        app.alerts.show({ type: 'success' }, msg);
        this.hide();
        setTimeout(() => window.location.reload(), 3000);
      })
      .catch((error) => {
        this.publishing = false;
        this.publishError = error.message || app.translator.trans('pianotell-recitals.forum.publish_error');
        m.redraw();
      });
  }
}
